import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/api";


const statusColors = {
  issued: "bg-purple-50 text-purple-700 border-purple-200",
  redeemed: "bg-purple-100 text-success border-success/30",
  expired: "bg-purple-50 text-ink/50 border-ink/10",
};

export default function RewardsPage() {
  const { authHeaders, isAdmin } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = (p = page, sf = statusFilter, cf = clientFilter) => {
    let url = `${API}/rewards?page=${p}&limit=20`;
    if (sf && sf !== "all") url += `&status=${sf}`;
    if (cf && cf !== "all") url += `&client_id=${cf}`;
    setLoading(true);
    axios.get(url, { headers: authHeaders() }).then((r) => {
      setRewards(r.data.rewards || []);
      setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/vendors`, { headers: authHeaders() }).then((r) => setVendors(r.data)).catch(() => {});
    }
    load();
  }, []);

  const handleRedeem = async (rewardId) => {
    await axios.put(`${API}/rewards/${rewardId}/redeem`, {}, { headers: authHeaders() });
    load();
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6" data-testid="rewards-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Rewards</h1>
        <p className="text-sm text-ink/50 mt-0.5">{total.toLocaleString()} total rewards</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); load(1, v, clientFilter); }}>
          <SelectTrigger className="w-40 h-9 text-sm" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="issued">Issued</SelectItem>
            <SelectItem value="redeemed">Redeemed</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Select value={clientFilter} onValueChange={(v) => { setClientFilter(v); setPage(1); load(1, statusFilter, v); }}>
          <SelectTrigger className="w-44 h-9 text-sm" data-testid="rewards-vendor-filter">
            <SelectValue placeholder="All Vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vendors</SelectItem>
            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-md border border-ink/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface">
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Vendor</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Value</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Issued</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Expires</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-ink/40 text-sm">Loading…</TableCell></TableRow>
            ) : rewards.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-ink/40 text-sm">No rewards found</TableCell></TableRow>
            ) : (
              rewards.map((r) => (
                <TableRow key={r.id} data-testid={`reward-row-${r.id}`}>
                  <TableCell className="text-sm text-ink/80">{r.vendor_name || "—"}</TableCell>
                  <TableCell>
                    <p className="text-sm">{r.customer_name || "Unknown"}</p>
                    <p className="text-xs text-ink/40">{r.customer_email || "—"}</p>
                  </TableCell>
                  <TableCell className="font-mono text-sm font-bold text-success">£{r.value_gbp?.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${statusColors[r.status] || "bg-surface"}`}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-ink/40">{r.issued_at?.slice(0, 10)}</TableCell>
                  <TableCell className="font-mono text-xs text-ink/40">{r.expiry_date?.slice(0, 10)}</TableCell>
                  <TableCell>
                    {r.status === "issued" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRedeem(r.id)}
                        data-testid={`redeem-reward-${r.id}`}
                        className="h-7 text-xs gap-1 text-success border-success/40 hover:bg-purple-50"
                      >
                        <CheckCircle2 size={12} /> Mark Redeemed
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-ink/50">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => { setPage(page - 1); load(page - 1); }} data-testid="prev-page">
              <ChevronLeft size={14} />
            </Button>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => { setPage(page + 1); load(page + 1); }} data-testid="next-page">
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
